import { useEffect, useRef, useState } from "react";
import { Minus, Loader2, Check, Pencil, Settings } from "lucide-react";
import type { PanneauDetail } from "../api/types";
import { AttachableChip } from "./AttachableChip";
import { CommandeTableau } from "./CommandeTableau";
import { InventaireTableau } from "./InventaireTableau";
import { LabelsChip } from "./LabelsChip";
import { NewPanneauModal } from "./NewPanneauModal";
import { NotesChip } from "./NotesChip";
import {
  useArchivePanneau,
  useRejectProposal,
  useUpdatePanneau,
  useValidateAll,
  useValidateProposal,
} from "../api/queries";

interface PanneauProps {
  conv: string;
  panneau: PanneauDetail;
  /** Optional close handler. When provided, the close button delegates
   *  to it instead of running its own archive mutation — App owns the
   *  flow so it can fire an undo toast. Falls back to local archive
   *  for callers that don't need the toast (e.g. ExpandedPanneauModal). */
  onArchive?: (panneau: PanneauDetail) => void;
}

/**
 * The canonical panneau component — header + Personnes chips +
 * close/duplicate/expand buttons; left chip clusters (Labels, Notes);
 * main contenu (a tableau matching the panneau's kind); bottom chip
 * clusters (Espaces, Lieux); Valider button bottom-right.
 *
 * Phase 1 implements only commande_sortante. Other kinds render
 * placeholder text so we know they're acknowledged but unimplemented.
 */
export function Panneau({ conv, panneau, onArchive }: PanneauProps) {
  const [validatingId, setValidatingId] = useState<string | null>(null);
  const [rejectingId, setRejectingId] = useState<string | null>(null);
  const [editOpen, setEditOpen] = useState(false);
  const archive = useArchivePanneau(conv);
  const validateAll = useValidateAll(panneau.id);
  const validateProposal = useValidateProposal(panneau.id);
  const reject = useRejectProposal(panneau.id);
  const updatePanneau = useUpdatePanneau(panneau.id, conv);

  const onValidate = async (proposalId: string) => {
    setValidatingId(proposalId);
    try {
      await validateProposal.mutateAsync(proposalId);
    } finally {
      setValidatingId((cur) => (cur === proposalId ? null : cur));
    }
  };

  const onReject = async (proposalId: string) => {
    setRejectingId(proposalId);
    try {
      await reject.mutateAsync(proposalId);
    } finally {
      setRejectingId((cur) => (cur === proposalId ? null : cur));
    }
  };

  return (
    <section className="rounded-2xl border border-black/10 dark:border-white/10 bg-white dark:bg-surface-dark shadow-sm">
      {/* Top bar — Nom + Personnes chips + close button */}
      <header className="flex items-center justify-between px-4 py-3 border-b border-black/10 dark:border-white/10">
        <PanneauNameEdit
          name={panneau.name}
          onSave={(name) => updatePanneau.mutateAsync({ name })}
          pending={updatePanneau.isPending}
        />
        <div className="flex items-center gap-4">
          <AttachableChip
            kind="personnes"
            panneauId={panneau.id}
            organisation={panneau.organisation}
            attached={panneau.personnes}
            pending={panneau.pending_proposals}
            side="right"
          />
          <button
            type="button"
            onClick={() => setEditOpen(true)}
            title="Modifier le filtre / le nom du panneau"
            className="h-7 w-7 rounded-full bg-white dark:bg-surface-dark ring-1 ring-black/10 dark:ring-white/10 text-muted dark:text-muted-dark hover:text-ink dark:hover:text-ink-dark hover:ring-ink/40 dark:hover:ring-white/40 flex items-center justify-center"
            aria-label="Modifier le panneau"
          >
            <Settings className="h-3.5 w-3.5" />
          </button>
          <button
            type="button"
            onClick={() => {
              if (onArchive) onArchive(panneau);
              else archive.mutate(panneau.id);
            }}
            disabled={archive.isPending}
            title="Fermer le panneau"
            className="h-7 w-7 rounded-full bg-ink/90 text-white flex items-center justify-center hover:bg-ink disabled:opacity-50"
            aria-label="Fermer le panneau"
          >
            {archive.isPending ? (
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
            ) : (
              <Minus className="h-3.5 w-3.5" />
            )}
          </button>
        </div>
      </header>

      <NewPanneauModal
        open={editOpen}
        onClose={() => setEditOpen(false)}
        panneau={panneau}
        conv={conv}
      />

      {/* Body. On phones we stack: chips above the contenu so the
          tableau gets the full width. From sm: up we go back to the
          two-column layout from `interface_26_2.pdf`. */}
      <div className="flex flex-col sm:grid sm:grid-cols-[auto_1fr] gap-3 sm:gap-4 px-3 sm:px-4 py-3 sm:py-4">
        {/* Left chip column: Labels + Notes (row on mobile, column on sm+) */}
        <div className="flex flex-row sm:flex-col gap-4 pt-1 sm:pt-2 sm:min-w-[10rem]">
          <LabelsChip
            panneauId={panneau.id}
            organisation={panneau.organisation}
            attached={panneau.labels}
            pending={panneau.pending_proposals}
          />
          <NotesChip
            panneauId={panneau.id}
            author="Dinh"
            notes={panneau.notes}
            pending={panneau.pending_proposals}
          />
        </div>

        {/* Contenu — `min-w-0` overrides the grid track's default
            `min-width: auto` so wide markdown/table content can scroll
            horizontally inside this cell instead of pushing the panneau
            wider than its container.
            `@container` (Tailwind container-queries plugin) lets the
            tableaux inside react to *this div's* width — needed so the
            CommandeTableau collapses to its mobile layout when the
            panneau is narrow because of split-view, not just because of
            a phone-sized viewport. */}
        <div className="@container min-h-[12rem] min-w-0 rounded-xl bg-canvas dark:bg-canvas-dark border border-black/5 dark:border-white/10 p-4">
          {panneau.kind === "commande_sortante" && (
            <CommandeTableau
              proposals={panneau.pending_proposals}
              onValidate={onValidate}
              onReject={onReject}
              validating={validatingId}
              rejecting={rejectingId}
            />
          )}
          {panneau.kind === "inventaire" && (
            <InventaireTableau
              stock={panneau.current_stock}
              pending={panneau.pending_proposals}
              onReject={onReject}
              rejecting={rejectingId}
              organisation={panneau.organisation}
              panneauId={panneau.id}
            />
          )}
          {panneau.kind === "rapport" && (
            <div className="text-muted dark:text-muted-dark text-sm">
              Le type <code>rapport</code> sera implémenté en Phase 4.
            </div>
          )}
        </div>
      </div>

      {/* Bottom: Espaces + Lieux chips on the left, Valider button on the right */}
      <footer className="flex items-end justify-between px-4 pb-4 pt-2 border-t border-black/10 dark:border-white/10">
        <div className="flex gap-6">
          <AttachableChip
            kind="espaces"
            panneauId={panneau.id}
            organisation={panneau.organisation}
            attached={panneau.espaces}
            pending={panneau.pending_proposals}
            placement="top"
          />
          <AttachableChip
            kind="lieux"
            panneauId={panneau.id}
            organisation={panneau.organisation}
            attached={panneau.lieux}
            pending={panneau.pending_proposals}
            placement="top"
          />
        </div>
        <button
          type="button"
          onClick={() => validateAll.mutate()}
          disabled={
            validateAll.isPending ||
            panneau.pending_proposals.length === 0
          }
          aria-label="Valider toutes les propositions"
          className="px-4 py-2 rounded-full bg-ink text-white text-sm font-semibold flex items-center gap-2 hover:bg-ink/90 disabled:bg-ink/40 disabled:cursor-not-allowed"
        >
          {validateAll.isPending ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <Check className="h-4 w-4" />
          )}
          {panneau.pending_proposals.length === 0
            ? "Aucune proposition"
            : `Valider tout (${panneau.pending_proposals.length})`}
        </button>
      </footer>
    </section>
  );
}

interface PanneauNameEditProps {
  name: string;
  onSave: (name: string) => Promise<unknown>;
  pending: boolean;
}

function PanneauNameEdit({ name, onSave, pending }: PanneauNameEditProps) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(name);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!editing) setDraft(name);
  }, [editing, name]);

  useEffect(() => {
    if (editing) inputRef.current?.select();
  }, [editing]);

  const commit = async () => {
    const trimmed = draft.trim();
    if (!trimmed || trimmed === name) {
      setEditing(false);
      setDraft(name);
      return;
    }
    try {
      await onSave(trimmed);
    } catch {
      /* swallowed; v1 has no toast */
    } finally {
      setEditing(false);
    }
  };

  const onKey = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      e.preventDefault();
      commit();
    } else if (e.key === "Escape") {
      e.preventDefault();
      setEditing(false);
      setDraft(name);
    }
  };

  if (editing) {
    return (
      <input
        ref={inputRef}
        type="text"
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onBlur={commit}
        onKeyDown={onKey}
        disabled={pending}
        maxLength={200}
        className="bun-textbox text-sm font-semibold flex-1 min-w-0 mr-3 px-2 py-1 rounded border border-ink/30 dark:border-white/30 focus:outline-none focus:ring-1 focus:ring-ink dark:focus:ring-white"
        aria-label="Renommer le panneau"
      />
    );
  }
  return (
    <button
      type="button"
      onClick={() => setEditing(true)}
      className="group inline-flex items-center gap-1.5 text-sm font-semibold flex-1 min-w-0 mr-3 hover:text-ink/70 dark:hover:text-ink-dark/70"
      title={`${name} — clic pour renommer`}
    >
      <span className="truncate text-left">{name}</span>
      <Pencil className="h-3 w-3 text-muted dark:text-muted-dark shrink-0 opacity-40 group-hover:opacity-100 transition-opacity" />
    </button>
  );
}
